import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { OptiBaySessionRepair } from '@/types/optibay';

const sb = supabase as any;

export function useSessionRepairs(sessionId: string | undefined) {
  return useQuery({
    queryKey: ['optibay-session-repairs', sessionId],
    queryFn: async (): Promise<OptiBaySessionRepair[]> => {
      if (!sessionId) return [];
      const { data, error } = await sb
        .from('optibay_session_repairs')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as OptiBaySessionRepair[];
    },
    enabled: !!sessionId,
  });
}

export interface AddRepairParams {
  sessionId: string;
  interpretationId?: string;
  repairDescription: string;
  repairCategory?: string;
  partsNeeded?: string;
  estimatedMinutes?: number;
}

export function useAddRepair() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: AddRepairParams) => {
      const { data, error } = await sb
        .from('optibay_session_repairs')
        .insert({
          session_id: params.sessionId,
          interpretation_id: params.interpretationId || null,
          repair_description: params.repairDescription,
          repair_category: params.repairCategory || null,
          parts_needed: params.partsNeeded || null,
          estimated_minutes: params.estimatedMinutes ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as OptiBaySessionRepair;
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-repairs', data.session_id] });
    },
  });
}

export function useUpdateRepair() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: {
      id: string;
      updates: Partial<Pick<OptiBaySessionRepair, 'repair_description' | 'repair_category' | 'parts_needed' | 'estimated_minutes' | 'status' | 'completed_at'>>;
    }) => {
      const { data, error } = await sb
        .from('optibay_session_repairs')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as OptiBaySessionRepair;
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-repairs', data.session_id] });
    },
  });
}

export function useRemoveRepair() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, sessionId }: { id: string; sessionId: string }) => {
      const { error } = await sb
        .from('optibay_session_repairs')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { sessionId };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-repairs', result.sessionId] });
    },
  });
}
